import { useEffect } from 'react';
import { type GeolocationInfo } from '@cx-web/domain-types';
import { escapeJsonForInlineScript } from '@cx-web/utils';

import { type Repub } from '@cx-modules/constants';

/**
 * GTM dimensions are strings, but the geo values are free-form upstream (`city_name` /
 * `subdivision_name`) and can be absent at runtime despite the type. Absent becomes `''` — the same
 * empty value `getFallbackGeolocation` uses — never the string `"undefined"`.
 */
const toDimension = (value: unknown): string => (value == null ? '' : String(value));

export const GoogleTagManagerHead = ({
  geolocationInfo,
  gtmId,
  repub,
  shouldRender,
  shouldRenderPostHydration,
  userGroup = '',
  userId = '',
}: {
  geolocationInfo: GeolocationInfo;
  gtmId: string;
  repub: Repub;
  shouldRender: boolean;
  shouldRenderPostHydration: boolean;
  userGroup: string | undefined;
  userId: string | undefined;
}) => {
  useEffect(() => {
    if (!shouldRender || !shouldRenderPostHydration) {
      return;
    }

    const gtmScript = document.createElement('script');
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    gtmScript.async = true;
    document.head.appendChild(gtmScript);
  }, [gtmId, shouldRender, shouldRenderPostHydration]);

  const { countryCode, geoCity, geoCountry, geoRegion, subdivisionCode } = geolocationInfo;

  // Serialised, never interpolated: one apostrophe in a city name (Coeur d'Alene) used to make this
  // whole script a SyntaxError, so nothing created window.dataLayer and every later push was lost.
  // See tests/GoogleTagManagerHead.test.tsx.
  const dimensions = {
    country: toDimension(countryCode),
    countryCode: toDimension(countryCode),
    repub: toDimension(repub),
    state: toDimension(subdivisionCode),
    geoCity: toDimension(geoCity),
    geoRegion: toDimension(geoRegion),
    geoCountry: toDimension(geoCountry),
    userId: toDimension(userId),
    userGroup: toDimension(userGroup),
  };

  return (
    <>
      <script
        dangerouslySetInnerHTML={{
          __html: `
						window.dataLayer = window.dataLayer || [];
						window.dataLayer.push(${escapeJsonForInlineScript(dimensions)});
						window.dataLayer.push({
							'gtm.start': new Date().getTime(),
							event: 'gtm.js'
						});
					`,
        }}
        id="gtag-init"
      />
      {shouldRender && !shouldRenderPostHydration && (
        <script async={true} src={`https://www.googletagmanager.com/gtm.js?id=${gtmId}`} />
      )}
    </>
  );
};
